// Sub-project D cache-bust 2026-05-17T04:30
/**
 * wc-state.js — Multi-gen localStorage layer + session-id helper
 *
 * Extracted from wolden-customize.js lines 248-547 (WC.gens IIFE) and
 * lines 602-607 (tempGenerationId) + lines 1204-1218 (getOrCreateSessionId).
 *
 * SCHEMA: version 2 — DO NOT bump here; Sub-project C owns the v3 migration.
 *
 * Public exports match the original WC.gens.* surface exactly, plus two
 * standalone helpers promoted to module exports:
 *   getSessionId()      — reads/writes wolden_session_id cookie (1-year TTL)
 *   tempGenerationId()  — client-side UUID fallback for generation IDs
 *
 * Cross-module dependencies:
 *   ./wc-analytics.js  — log()  (used inside write() and bulkImportOnSignIn())
 */

import { log } from './wc2-analytics.js';



// ─── Schema constants ──────────────────────────────────────────
const STORAGE_PREFIX  = 'wc.gens.';
const SCHEMA_VERSION  = 3;                    // v3: Gen-typed API added in Sub-project C Task C.1
const TTL_MS          = 24 * 60 * 60 * 1000; // 24 h
const SESSION_COOKIE  = 'wolden_session_id';

// ─── Namespace key helpers ─────────────────────────────────────

export function getNamespaceKey(product) {
  if (!product) return null;
  return product.artId || product.productHandle || product.handle || null;
}

export function getNamespaceType(product) {
  if (!product) return null;
  return product.artId ? 'art_id' : 'product_handle';
}

// ─── LocalStorage read / write ─────────────────────────────────

export function read(namespaceKey) {
  if (!namespaceKey || typeof localStorage === 'undefined') return null;
  try {
    var raw = localStorage.getItem(STORAGE_PREFIX + namespaceKey);
    if (!raw) return null;
    var data = JSON.parse(raw);
    if (data.schemaVersion !== SCHEMA_VERSION) return null;
    var now = Date.now();
    data.gens = (data.gens || []).filter(function (g) {
      return new Date(g.expiresAt).getTime() > now;
    });
    if (data.gens.length === 0) {
      try { localStorage.removeItem(STORAGE_PREFIX + namespaceKey); } catch (e) {}
      return null;
    }
    return data;
  } catch (e) { return null; }
}

function write(namespaceKey, data) {
  if (!namespaceKey || typeof localStorage === 'undefined') return;
  try { localStorage.setItem(STORAGE_PREFIX + namespaceKey, JSON.stringify(data)); }
  catch (e) { log('LocalStorage write failed:', e && e.message); }
}

// ─── Generation CRUD ───────────────────────────────────────────

export function add(namespaceKey, namespaceType, entryProductHandle, entryProductType, gen) {
  if (!namespaceKey) return null;
  var existing = read(namespaceKey) || {
    schemaVersion: SCHEMA_VERSION,
    namespaceKey: namespaceKey,
    namespaceType: namespaceType,
    lastEntryProductHandle: entryProductHandle,
    lastEntryProductType: entryProductType,
    gens: [],
    activeGenId: null,
  };
  existing.lastEntryProductHandle = entryProductHandle || existing.lastEntryProductHandle;
  existing.lastEntryProductType = entryProductType || existing.lastEntryProductType;

  var dupIdx = -1;
  for (var i = 0; i < existing.gens.length; i++) {
    if (existing.gens[i].id === gen.id) { dupIdx = i; break; }
  }
  if (dupIdx === -1) {
    gen.ordinal = existing.gens.length + 1;
    gen.expiresAt = new Date(Date.now() + TTL_MS).toISOString();
    existing.gens.push(gen);
  }
  existing.activeGenId = gen.id;
  write(namespaceKey, existing);
  return existing;
}

export function setActive(namespaceKey, genId) {
  var data = read(namespaceKey);
  if (!data) return null;
  data.activeGenId = genId;
  write(namespaceKey, data);
  return data;
}

export function list(namespaceKey) {
  var data = read(namespaceKey);
  return data ? data.gens : [];
}

export function getActive(namespaceKey) {
  var data = read(namespaceKey);
  if (!data) return null;
  for (var i = 0; i < data.gens.length; i++) {
    if (data.gens[i].id === data.activeGenId) return data.gens[i];
  }
  return null;
}

// ─── UI helper ────────────────────────────────────────────────

export function ordinalLabel(n) {
  var s = ['th', 'st', 'nd', 'rd'];
  var v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// ─── Cross-namespace scan helpers ─────────────────────────────

/** Merge fresh mockup URLs into a saved gen's snapshot by generation id. */
export function updateMockups(genId, mockups) {
  if (!genId || !mockups || typeof localStorage === 'undefined') return false;
  try {
    for (var i = 0; i < localStorage.length; i++) {
      var k = localStorage.key(i);
      if (!k || k.indexOf(STORAGE_PREFIX) !== 0) continue;
      var raw = localStorage.getItem(k);
      if (!raw) continue;
      var data;
      try { data = JSON.parse(raw); } catch (e) { continue; }
      if (data.schemaVersion !== SCHEMA_VERSION || !Array.isArray(data.gens)) continue;
      for (var j = 0; j < data.gens.length; j++) {
        var g = data.gens[j];
        if (g && g.id === genId) {
          g.snapshot = g.snapshot || {};
          if (mockups.paintKit)   g.snapshot.paintKitMockup = mockups.paintKit;
          if (mockups.pbnBw)      g.snapshot.pbnBwUrl       = mockups.pbnBw;
          if (mockups.pbnColored) g.snapshot.pbnColoredUrl  = mockups.pbnColored;
          // 2026-07-02 QA round-4 (additive) — the flows' straight-on poll
          // persists the front-shot URL here so a reload rebuilds the same
          // 3-slot canvas deck. Existing callers never pass this key, so
          // behavior is unchanged for the champion/monolith paths.
          if (mockups.straightOn) g.snapshot.straightOnMockupUrl = mockups.straightOn;
          try { localStorage.setItem(k, JSON.stringify(data)); } catch (e) {}
          return true;
        }
      }
    }
  } catch (e) { /* ignore */ }
  return false;
}

/**
 * Phase C — cross-PDP hydration helper.
 * Walk all wc.gens.* keys to find a gen whose orchestrator child product
 * matches the given handle. Returns { namespaceKey, gen, format, [size], [color] }
 * or null.
 */
export function findByChildHandle(handle) {
  if (!handle || typeof localStorage === 'undefined') return null;
  try {
    for (var i = 0; i < localStorage.length; i++) {
      var k = localStorage.key(i);
      if (!k || k.indexOf(STORAGE_PREFIX) !== 0) continue;
      var data = read(k.slice(STORAGE_PREFIX.length));
      if (!data || !Array.isArray(data.gens)) continue;
      for (var j = 0; j < data.gens.length; j++) {
        var g = data.gens[j];
        var snap = g.snapshot || {};
        // Check canvas children (one per size)
        if (snap.canvasShopify) {
          for (var sz in snap.canvasShopify) {
            if (snap.canvasShopify[sz] && snap.canvasShopify[sz].shopifyHandle === handle) {
              return { namespaceKey: data.namespaceKey, gen: g, format: 'canvas', size: sz };
            }
          }
        }
        // Check canvas-framed children (one product per size, 4 color variants each).
        // Color isn't recoverable from the handle alone — we default to White.
        if (snap.canvasFramedShopify) {
          for (var fsz in snap.canvasFramedShopify) {
            if (snap.canvasFramedShopify[fsz] && snap.canvasFramedShopify[fsz].shopifyHandle === handle) {
              return { namespaceKey: data.namespaceKey, gen: g, format: 'canvas_framed', size: fsz, color: 'White' };
            }
          }
        }
        // Check paint kit (single handle, multiple variants)
        if (snap.paintKitShopify && snap.paintKitShopify.shopifyHandle === handle) {
          return { namespaceKey: data.namespaceKey, gen: g, format: 'paint_kit' };
        }
      }
    }
  } catch (e) { /* ignore */ }
  return null;
}

// ─── Tier 2 — Bubble DB / Lambda (cross-device sync) ──────────

function getGenStoreUrl() {
  var cfg = window.WoldenCustomizeConfig || {};
  return cfg.genStoreUrl || '';
}

function getCustomerId() {
  // Mirror getShopifyCustomerId but available inside this module.
  return (window.WoldenShopifyCustomerId && String(window.WoldenShopifyCustomerId)) || null;
}

function lambdaPost(path, body, opts) {
  var url = getGenStoreUrl();
  if (!url) return Promise.resolve(null);
  opts = opts || {};
  var payload = Object.assign({ path: path }, body || {});
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(function (r) {
    if (!r.ok) throw new Error('gen-store ' + path + ' returned ' + r.status);
    return r.json();
  }).catch(function (e) {
    if (!opts.silent) { try { console.warn('[wolden-gen-store]', path, 'error:', e && e.message); } catch (_) {} }
    return null;
  });
}

/** Fire-and-forget: persist a single gen to the cloud after local save. */
export function syncSaveToCloud(gen) {
  var customerId = getCustomerId();
  if (!getGenStoreUrl() || !customerId) return;
  var wireGen = {
    gen_id: gen.id,
    namespace_key: gen.namespace_key,
    namespace_type: gen.namespace_type,
    entry_product_handle: gen.entry_product_handle,
    entry_product_type: gen.entry_product_type,
    ordinal: gen.ordinal,
    created_at: gen.createdAt,
    image_url: gen.imageUrl,
    thumb_url: gen.thumbUrl,
    pet_photo_url: gen.petPhotoUrl,
    prompt_hash: gen.promptHash || '',
    status: gen.status || 'complete',
    snapshot_json: gen.snapshot ? JSON.stringify(gen.snapshot) : '',
  };
  lambdaPost('save', { customer_id: customerId, gen: wireGen }, { silent: true });
}

export function syncDeleteToCloud(genId) {
  var customerId = getCustomerId();
  if (!getGenStoreUrl() || !customerId) return;
  lambdaPost('delete', { customer_id: customerId, gen_id: genId }, { silent: true });
}

/**
 * One-time bulk import: when an anon user with LocalStorage gens signs in,
 * push everything to the Lambda. Idempotent — flagged per customer_id.
 */
export function bulkImportOnSignIn() {
  var customerId = getCustomerId();
  if (!getGenStoreUrl() || !customerId) return Promise.resolve();
  var flagKey = 'wc_gens_imported_for_' + customerId;
  try {
    if (localStorage.getItem(flagKey) === '1') return Promise.resolve();
  } catch (e) { return Promise.resolve(); }

  // Collect all wc.gens.* records
  var allGens = [];
  try {
    for (var i = 0; i < localStorage.length; i++) {
      var k = localStorage.key(i);
      if (!k || k.indexOf(STORAGE_PREFIX) !== 0) continue;
      var data = read(k.slice(STORAGE_PREFIX.length));
      if (!data) continue;
      (data.gens || []).forEach(function (g) {
        if (g.status === 'deleted') return;
        allGens.push({
          gen_id: g.id,
          namespace_key: data.namespaceKey,
          namespace_type: data.namespaceType,
          entry_product_handle: data.lastEntryProductHandle,
          entry_product_type: data.lastEntryProductType,
          ordinal: g.ordinal,
          created_at: g.createdAt,
          image_url: g.imageUrl,
          thumb_url: g.thumbUrl,
          pet_photo_url: g.petPhotoUrl,
          status: g.status || 'complete',
          snapshot_json: g.snapshot ? JSON.stringify(g.snapshot) : '',
        });
      });
    }
  } catch (e) { return Promise.resolve(); }

  if (allGens.length === 0) {
    try { localStorage.setItem(flagKey, '1'); } catch (e) {}
    return Promise.resolve();
  }

  log('bulk-importing', allGens.length, 'gens for customer', customerId);
  return lambdaPost('import', { customer_id: customerId, gens: allGens }, { silent: false })
    .then(function (res) {
      if (res && res.ok) {
        try { localStorage.setItem(flagKey, '1'); } catch (e) {}
        log('bulk import done — imported', res.imported, 'skipped', res.skipped);
      }
      return res;
    });
}

// ─── Session-id helper ─────────────────────────────────────────

/**
 * Read the wolden_session_id cookie; create + persist a new one if absent.
 * Cookie TTL: 1 year (max-age). Identical logic to the monolith's
 * getOrCreateSessionId() at line 1204 — preserved bit-for-bit.
 */
export function getSessionId() {
  try {
    var match = document.cookie.match(new RegExp('(?:^|; )' + SESSION_COOKIE + '=([^;]+)'));
    if (match) return decodeURIComponent(match[1]);
  } catch (e) { /* swallow */ }
  var newId = (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  try {
    var maxAge = 60 * 60 * 24 * 365; // 1 year
    document.cookie = SESSION_COOKIE + '=' + encodeURIComponent(newId) +
      '; path=/; max-age=' + maxAge + '; SameSite=Lax';
  } catch (e) { /* swallow */ }
  return newId;
}

// ─── Generation-id generator ───────────────────────────────────

/**
 * Generate a client-side generationId. Phase 4 will replace this with a real
 * Bubble-issued MidjourneyGeneration ID via the orchestrator.
 * Identical to the monolith's tempGenerationId() at line 602.
 */
export function tempGenerationId() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'gen-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Sub-project C Task C.1 — Gen-typed API ──────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * @typedef {Object} Gen
 * @property {string}  generationId
 * @property {string}  sourceArtUrl
 * @property {string}  generatedArtUrl
 * @property {string|null} watermarkedArtUrl
 * @property {string|null} highDpiArtUrl
 * @property {Object}  formats
 * @property {string}  activeFormat
 * @property {number}  generatedAt
 * @property {string}  parentProductHandle
 * @property {string|null} customerId
 * @property {string}  sessionId
 * @property {string}  source
 */

/**
 * @typedef {Object} FormatData
 * @property {Variant[]} variants
 * @property {string|null} shopifyProductId
 * @property {string|null} shopifyProductHandle
 * @property {Object<string,string>} mockupUrls
 */

/**
 * @typedef {Object} Variant
 * @property {string} shopifyVariantId
 * @property {string} title
 * @property {string} size
 * @property {string|null} color
 * @property {string|null} frameOption
 * @property {number} priceCents
 * @property {number|null} compareAtPriceCents
 * @property {string} frontMockupUrl
 * @property {string|null} contextMockupUrl
 */

// ─── Module-private state ──────────────────────────────────────

const _memCache    = new Map(); // generationId -> Gen
const _subscribers = new Map(); // generationId -> Set<fn>

// ─── Private normalize helpers ─────────────────────────────────

function _normalizeCanvas(raw) {
  // From inline orchestrator response: raw.canvasShopify[size] = { shopifyVariantId, shopifyHandle, shopifyProductId, ... }
  // From /fetch-gen Bubble response:   raw.CanvasPrint_ShopifyVariantID1_{size}, _ShopifyHandle1_{size}, _ShopifyProductID1_{size}
  // Mockups: raw.canvasMockups[size]  OR  raw.CanvasMockupURL_{size}
  const sizes = ['8x10', '16x20', '24x30'];
  const inlineCanvas = raw.canvasShopify || {};
  const variants = [];
  for (const size of sizes) {
    const inline = inlineCanvas[size];
    const variantId = (inline && inline.shopifyVariantId) || raw[`CanvasPrint_ShopifyVariantID1_${size}`] || null;
    if (!variantId) continue;
    variants.push({
      shopifyVariantId: String(variantId),
      title: size,
      size: size,
      color: null,
      frameOption: null,
      priceCents: 0,             // populated later via Shopify variant fetch — see spec § 4.6
      compareAtPriceCents: null,
      frontMockupUrl: (raw.canvasMockups && raw.canvasMockups[size]) || raw[`CanvasMockupURL_${size}`] || '',
      contextMockupUrl: null,
    });
  }
  return {
    variants,
    shopifyProductId:     (inlineCanvas['8x10'] && inlineCanvas['8x10'].shopifyProductId) || raw.CanvasPrint_ShopifyProductID1_8x10 || null,
    shopifyProductHandle: (inlineCanvas['8x10'] && inlineCanvas['8x10'].shopifyHandle)    || raw.CanvasPrint_ShopifyHandle1_8x10    || null,
    mockupUrls: {},              // legacy field; variants[] holds the canonical mockup URLs
  };
}

function _normalizeCanvasFramed(raw) {
  // From inline: raw.canvasFramedShopify[size] = { shopifyProductId, shopifyHandle, variantByColor: { White, Black, Natural, Espresso } }
  //              raw.canvasFramedMockups['Front_8x10_White'] | raw.canvasFramedMockups['Context_8x10_White']
  // From Bubble: raw.CanvasPrintFramed_ShopifyVariantID1_{size}_{color}
  //              raw.CanvasPrintFramedMockup_Front_{size}_{color} | raw.CanvasPrintFramedMockup_Context_{size}_{color}
  const sizes  = ['8x10', '16x20', '24x30'];
  const colors = ['White', 'Black', 'Natural', 'Espresso'];
  const inline = raw.canvasFramedShopify || {};
  const inlineMockups = raw.canvasFramedMockups || {};
  const variants = [];
  for (const size of sizes) {
    for (const color of colors) {
      const inlineSize = inline[size] || {};
      const variantId  = (inlineSize.variantByColor && inlineSize.variantByColor[color])
                       || raw[`CanvasPrintFramed_ShopifyVariantID1_${size}_${color}`]
                       || null;
      if (!variantId) continue;
      variants.push({
        shopifyVariantId: String(variantId),
        title: `${size} / ${color}`,
        size: size,
        color: color,
        frameOption: null,
        priceCents: 0,
        compareAtPriceCents: null,
        frontMockupUrl:   inlineMockups[`Front_${size}_${color}`]   || raw[`CanvasPrintFramedMockup_Front_${size}_${color}`]   || '',
        contextMockupUrl: inlineMockups[`Context_${size}_${color}`] || raw[`CanvasPrintFramedMockup_Context_${size}_${color}`] || null,
      });
    }
  }
  return {
    variants,
    shopifyProductId:     (inline['8x10'] && inline['8x10'].shopifyProductId) || raw.CanvasPrintFramed_ShopifyProductID1_8x10 || null,
    shopifyProductHandle: (inline['8x10'] && inline['8x10'].shopifyHandle)    || raw.CanvasPrintFramed_ShopifyHandle1_8x10    || null,
    mockupUrls: {},
  };
}

function _normalizePaintKit(raw) {
  // From inline: raw.paintKitShopify = { shopifyProductId, shopifyHandle, variants: { '16x20_NoFrame': id, ... } }
  //              raw.paintKitMockup (single URL string)
  // From Bubble: raw.PaintKit_ShopifyVariantID1_{size}_{opt}
  //              raw['PBN-kit-mockup']  (legacy field name with hyphens)
  const sizes     = ['16x20', '20x26', '24x30'];
  const frameOpts = ['NoFrame', 'PreFramed', 'DIY'];
  const inline    = raw.paintKitShopify || {};
  const inlineVariants = inline.variants || {};
  // Bubble stores PBN mockup under 'PBN-kit-mockup' (hyphens, not camel)
  const mockupUrl = raw.paintKitMockup || raw['PBN-kit-mockup'] || '';
  const variants  = [];
  for (const size of sizes) {
    for (const opt of frameOpts) {
      const variantId = inlineVariants[`${size}_${opt}`]
                      || raw[`PaintKit_ShopifyVariantID1_${size}_${opt}`]
                      || null;
      if (!variantId) continue;
      variants.push({
        shopifyVariantId: String(variantId),
        title: `${size} / ${opt}`,
        size: size,
        color: null,
        frameOption: opt,
        priceCents: 0,
        compareAtPriceCents: null,
        frontMockupUrl:   mockupUrl,
        contextMockupUrl: null,
      });
    }
  }
  return {
    variants,
    shopifyProductId:     inline.shopifyProductId  || raw.PaintKit_ShopifyProductID1  || null,
    shopifyProductHandle: inline.shopifyHandle      || raw.PaintKit_ShopifyHandle1     || null,
    mockupUrls: {},
  };
}

/**
 * Map either an orchestrator inline response OR a /fetch-gen Bubble response → Gen.
 * Inline shape has camelCase keys (generationId, canvasShopify, petImageUrl, …).
 * Bubble shape has PascalCase keys  (GenerationID, PetImageURL_Original, …).
 *
 * @param {Object} raw    Raw response object from either source
 * @param {string} source "inline" | "fetch-gen" | "cache"
 * @returns {Gen|null}
 */
function _normalize(raw, source) {
  if (!raw) return null;

  const gen = {
    generationId:        raw.GenerationID              || raw.generationId              || null,
    sourceArtUrl:        raw.PetImageURL_Original      || raw.petImageUrl               || raw.sourceArtUrl   || '',
    generatedArtUrl:     raw.ArtURL_2K_Clean           || raw.artDisplayUrl             || raw.generatedArtUrl || '',
    watermarkedArtUrl:   raw.ArtURL_2K_Watermarked     || raw.watermarkedArtUrl         || null,
    highDpiArtUrl:       raw.highDpiArtUrl             || null,
    activeFormat:        raw.activeFormat              || 'canvas',
    generatedAt:         raw.ArtCompletedAt ? Date.parse(raw.ArtCompletedAt) : (raw.generatedAt || Date.now()),
    parentProductHandle: raw.ShopifyProductHandle_Original
                       || raw.parentProductHandle
                       || (raw.productCtx && raw.productCtx.productHandle)
                       || '',
    customerId:          raw.ShopifyCustomerID         || raw.customerId                || null,
    sessionId:           raw.WoldenSessionID           || raw.sessionId                 || '',
    source:              source,
    formats: {
      canvas:         _normalizeCanvas(raw),
      canvas_framed:  _normalizeCanvasFramed(raw),
      paint_kit:      _normalizePaintKit(raw),
    },
  };
  if (!gen.generationId) return null;
  return gen;
}

// ─── Public Gen API ────────────────────────────────────────────

/** Memory cache lookup. Returns Gen | null. */
export function getGen(generationId) {
  if (!generationId) return null;
  return _memCache.get(generationId) || null;
}

/** Bool: is the gen in memory? */
export function hasGen(generationId) {
  return _memCache.has(generationId);
}

/** Upsert a gen. Writes to memory + localStorage. Fires subscribers. */
export function setGen(gen) {
  if (!gen || !gen.generationId) return;
  _memCache.set(gen.generationId, gen);
  // Persist via existing add() using parentProductHandle as namespace key.
  const namespaceKey = gen.parentProductHandle || 'default';
  try {
    add(namespaceKey, 'product_handle', namespaceKey, 'canvas', gen);
  } catch (e) { /* localStorage failure is non-fatal */ }
  _fireSubscribers(gen.generationId, gen);
}

/**
 * Returns the gen currently displayed on this page, or null.
 * Bridges via window.WC._currentResult during the legacy-coexistence period.
 * @returns {Gen|null}
 */
export function getActiveGen() {
  const id = (window.WC && window.WC._currentResult && window.WC._currentResult.generationId) || null;
  return getGen(id);
}

/**
 * Set the currently-displayed gen. Also writes to window.WC._currentResult bridge.
 * @param {Gen|null} gen
 */
export function setActiveGen(gen) {
  if (!gen) return;
  if (typeof window !== 'undefined') {
    window.WC = window.WC || {};
    window.WC._currentResult = gen;
  }
  setGen(gen); // also fires subscribers
}

/**
 * Subscribe to changes for a gen. Returns an unsubscribe function.
 * @param {string}   generationId
 * @param {function} fn   Called with (gen) on every setGen/setActiveGen
 * @returns {function}    Call to unsubscribe
 */
export function subscribe(generationId, fn) {
  if (!generationId || typeof fn !== 'function') return () => {};
  let set = _subscribers.get(generationId);
  if (!set) { set = new Set(); _subscribers.set(generationId, set); }
  set.add(fn);
  return () => { set.delete(fn); };
}

/**
 * List all non-expired gens, sorted desc by generatedAt.
 * @param {{ namespaceKey?: string }} [opts]
 * @returns {Gen[]}
 */
export function listGens({ namespaceKey } = {}) {
  if (namespaceKey) {
    const ns = read(namespaceKey);
    return (ns && ns.gens) ? ns.gens.slice() : [];
  }
  // Cross-namespace: scan all wc.gens.* localStorage keys
  const all = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith('wc.gens.')) continue;
      const ns = read(key.slice('wc.gens.'.length));
      if (ns && ns.gens) all.push(...ns.gens);
    }
  } catch (e) {}
  // Dedupe by generationId, sort desc
  const seen = new Set();
  const out  = [];
  all.sort((a, b) => (b.generatedAt || 0) - (a.generatedAt || 0));
  for (const g of all) {
    if (g.generationId && !seen.has(g.generationId)) {
      seen.add(g.generationId);
      out.push(g);
    }
  }
  return out;
}

function _fireSubscribers(generationId, gen) {
  const set = _subscribers.get(generationId);
  if (!set) return;
  for (const fn of set) {
    try { fn(gen); } catch (e) { /* swallow; subscriber crash shouldn't break state */ }
  }
}

// Also export the normalize helper so wc-hydration.js + wc-upload.js can use it:
export { _normalize as normalize };
